import { useState } from 'react'
import { Trash } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import {
  Badge,
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChoiceGroup,
  Field,
  IconButton,
  Input,
  ScanField,
  Sheet,
  useToast,
  type ScanMeta,
} from '@/ui'
import type { CloseRunPayload, RecordOutputPayload, VoidOutputPayload } from '../../commands'
import '../../commands'
import { liveOutputs, outputStream, runParentIds, type ProcessState, type RunOutputView, type RunView } from '../../projection'
import {
  checkFreshCode,
  closeGaps,
  flushProgress,
  needsSecondEntry,
  runFigures,
  tieFor,
  type CloseGap,
} from '../../model/rules'
import { ReadingField, YesNo, type Reading } from '../../components/bits'
import { useBoth, useRefusal } from '../../components/useText'
import { outputName } from '../../components/labels'
import { BalancePanel } from '../../components/RunCard'
import type { RunDraft, RunDraftUpdate } from './draft'

const VOID_REASONS: VoidOutputPayload['reason'][] = ['WRONG_OUTPUT', 'WRONG_WEIGHT', 'TAG_PROBLEM', 'OTHER']

/** Moisture typed as "9.4" → exact one-decimal string, or null when empty / invalid. */
function moistureString(raw: string): string | null {
  const m = /^(\d{1,2})(?:\.(\d))?$/.exec(raw.trim())
  return m ? `${m[1]}.${m[2] ?? '0'}` : null
}

/** F6.5 (with the F6.2 flush sack) — every sack tagged and weighed, None per output, Close run. */
export function WeighOutSection({
  state,
  run,
  draft,
  update,
}: {
  state: ProcessState
  run: RunView
  draft: RunDraft
  update: RunDraftUpdate
}) {
  const { t, locale } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const refusal = useRefusal()
  const both = useBoth()
  const step = state.steps.get(run.step_code)
  const outputs = step?.outputs ?? []
  const flush = flushProgress(run)
  const live = liveOutputs(run)

  const [key, setKey] = useState<string | null>(null)
  const [tag, setTag] = useState<{ code: string; entry: ScanMeta['entry'] } | null>(null)
  const defaultSack = state.pack.sack_types.find((s) => s.default_output) ?? state.pack.sack_types[0]
  const [sackId, setSackId] = useState<string | null>(defaultSack?.id ?? null)
  const [reading, setReading] = useState<Reading | null>(null)
  const [moisture, setMoisture] = useState('')
  const [formKey, setFormKey] = useState(0)
  const [saving, setSaving] = useState(false)
  const [voiding, setVoiding] = useState<RunOutputView | null>(null)
  const [voidReason, setVoidReason] = useState<VoidOutputPayload['reason'] | null>(null)
  const [closing, setClosing] = useState(false)

  const withZero = { ...run, zero_streams: draft.zero_streams }
  const gaps = closeGaps(withZero, step, !!draft.stopped_at)
  const sack = state.pack.sack_types.find((s) => s.id === sackId) ?? null
  const stream = key ? outputStream(step, key, run.stream) : run.stream
  const tie = tieFor(stream)
  const needMoisture = !!step?.needs_moisture && key !== 'FLUSH'
  const moist = moistureString(moisture)
  const canSave = !!(key && tag && sack && reading && reading.read_g > sack.tare_g && (!needMoisture || moist))
  const name = (k: string) => outputName(state, run.step_code, k, locale, t)

  const resetForm = () => {
    setTag(null)
    setReading(null)
    setMoisture('')
    setFormKey((n) => n + 1)
  }

  const save = async () => {
    if (!canSave || !key || !tag || !sack || !reading) return
    setSaving(true)
    try {
      const container_id = newId()
      const def = outputs.find((o) => o.key === key)
      const same = live.find((o) => o.stream_key === key)
      const payload: RecordOutputPayload = {
        container_id,
        unit_id: same?.unit_id ?? newId(),
        run_id: run.id,
        qr_payload: tag.code,
        entry: tag.entry,
        stream_key: key,
        grade: def?.grade ?? null,
        sack_type_id: sack.id,
        tare_g: sack.tare_g,
        weight_read_g: reading.read_g,
        confirmed_twice: reading.confirmed_twice,
        moisture_pct: needMoisture ? moist : null,
        tie,
      }
      await enqueueCommand({
        id: container_id,
        type: 'record_output',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      // A sack saved for an output takes back its 'nothing came out'.
      if (draft.zero_streams.includes(key))
        update((d) => ({ ...d, zero_streams: d.zero_streams.filter((z) => z !== key) }))
      toast.success(t('process.weighOut.saved', { name: name(key), kg: fmt.weight(reading.read_g - sack.tare_g) }))
      resetForm()
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  const voidSack = async () => {
    if (!voiding || !voidReason) return
    try {
      await enqueueCommand({
        type: 'void_output',
        payload: { run_id: run.id, container_id: voiding.container_id, reason: voidReason } satisfies VoidOutputPayload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      setVoiding(null)
      setVoidReason(null)
      toast.success(t('process.weighOut.voided'))
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    }
  }

  const close = async () => {
    if (gaps.length || !draft.stopped_at) return
    setClosing(true)
    try {
      const fig = runFigures(run, step)
      const params: CloseRunPayload['params'] = {}
      for (const [k, v] of Object.entries(draft.params)) if (v.trim()) params[k] = v.trim()
      const payload: CloseRunPayload = {
        run_id: run.id,
        ended_at: draft.stopped_at,
        zero_streams: draft.zero_streams,
        params,
        phone_totals: {
          input_g: fig.input_g,
          output_g: fig.flush_g + fig.product_g + fig.byproduct_g + fig.reject_g,
          bags_in: run.inputs.length,
          sacks_out: live.length,
        },
      }
      await enqueueCommand({ type: 'close_run', payload, actorId: actor.user_id, parentIds: runParentIds(run) })
      toast.success(t('process.close.done'))
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setClosing(false)
    }
  }

  const gapText = (g: CloseGap) => {
    switch (g.kind) {
      case 'FLUSH_SHORT':
        return t('process.close.gap.FLUSH_SHORT', { kg: fmt.weight(g.short_g) })
      case 'OUTPUT_MISSING':
        return t('process.close.gap.OUTPUT_MISSING', { name: name(g.key) })
      case 'BAGS_NOT_IN':
        return t('process.close.gap.BAGS_NOT_IN', { n: g.count })
      default:
        return t(`process.close.gap.${g.kind}` as MessageKey)
    }
  }

  const buttons = [
    ...(run.flush_required ? [{ key: 'FLUSH', label: name('FLUSH'), disabled: false }] : []),
    ...outputs.map((o) => ({ key: o.key, label: locale === 'hi' ? o.hi : o.en, disabled: !flush.met })),
  ]

  return (
    <div className="flex flex-col gap-4">
      {run.flush_required && (
        <Banner tone={flush.met ? 'success' : 'warn'} title={<BilingualLabel k="process.flush.title" />}>
          {flush.met
            ? t('process.flush.met', { kg: fmt.weight(flush.flush_g) })
            : both('process.flush.more', { kg: fmt.weight(flush.short_g) })}
        </Banner>
      )}

      <Field label={<BilingualLabel k="process.weighOut.output" />} required>
        <div className="grid grid-cols-2 gap-2">
          {buttons.map((b) => (
            <button
              key={b.key}
              type="button"
              disabled={b.disabled}
              onClick={() => {
                setKey(b.key)
                resetForm()
              }}
              className={[
                'min-h-touch-lg rounded-chip border px-2 text-[15px] font-bold transition-colors disabled:opacity-40',
                key === b.key ? 'border-ink bg-ink text-paper' : 'border-ink/40 bg-surface',
                b.key === 'FLUSH' && key !== b.key ? 'border-danger text-danger' : '',
              ].join(' ')}
            >
              {b.label}
            </button>
          ))}
        </div>
      </Field>

      {key && (
        <Card key={formKey} className="border-ink">
          <CardHeader
            eyebrow={<BilingualLabel k="process.weighOut.newSack" />}
            title={name(key)}
            actions={
              tie === 'CONV' ? (
                <Badge tone="danger">{t('process.tie.red')}</Badge>
              ) : (
                <Badge tone="ok">{t('process.tie.none')}</Badge>
              )
            }
          />
          <CardBody className="flex flex-col gap-3">
            {tie === 'CONV' && <Banner tone="error">{both('process.tie.redBanner')}</Banner>}
            <Field label={<BilingualLabel k="process.weighOut.tag" />} required>
              <ScanField
                value={tag?.code ?? null}
                sheetTitle={<BilingualLabel k="process.weighOut.tag" />}
                validate={(c) => {
                  const v = checkFreshCode(state, c, 'OUTPUT_BAG', actor.user_id)
                  return v.ok ? null : refusal(v.reason)
                }}
                onChange={(c, meta) => setTag(c ? { code: c.trim().toUpperCase(), entry: meta?.entry ?? 'SCAN' } : null)}
              />
            </Field>
            <Field label={<BilingualLabel k="process.weighOut.sackType" />} required>
              <ChoiceGroup
                options={state.pack.sack_types.map((s) => ({
                  value: s.id,
                  label: `${s.name} · ${fmt.weight(s.tare_g)}`,
                }))}
                value={sackId}
                onChange={setSackId}
                layout="stack"
              />
            </Field>
            <ReadingField
              label={<BilingualLabel k="process.weighOut.weight" />}
              hint={sack ? t('process.weighOut.weightHint', { tare: fmt.weight(sack.tare_g) }) : undefined}
              needsTwice={(g) => needsSecondEntry(g, state.pack.settings)}
              onChange={setReading}
            />
            {needMoisture && (
              <Field label={<BilingualLabel k="process.moisture" />} required>
                <Input
                  size="touch"
                  inputMode="decimal"
                  value={moisture}
                  onChange={(e) => setMoisture(e.target.value.replace(/[^\d.]/g, '').slice(0, 4))}
                />
              </Field>
            )}
            {reading && sack && (
              <p className="font-mono text-[15px] tabular">
                {t('process.weighOut.net', { kg: fmt.weight(reading.read_g - sack.tare_g) })}
              </p>
            )}
            <Button size="xl" fullWidth disabled={!canSave} loading={saving} onClick={() => void save()}>
              <BilingualLabel k="process.weighOut.save" />
            </Button>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader eyebrow={t('process.weighOut.byOutput')} />
        <CardBody className="py-1">
          <ul>
            {[...(run.flush_required ? ['FLUSH'] : []), ...outputs.map((o) => o.key)].map((k) => {
              const sacks = live.filter((o) => o.stream_key === k)
              const zero = draft.zero_streams.includes(k)
              return (
                <li key={k} className="border-b border-rule/70 py-2.5 last:border-b-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-bold">{name(k)}</span>
                    <span className="font-mono text-[14px] tabular">
                      {sacks.length > 0
                        ? `${t('process.sacksN', { n: sacks.length })} · ${fmt.weight(sacks.reduce((a, o) => a + o.net_g, 0))}`
                        : zero
                          ? t('process.weighOut.zero')
                          : '—'}
                    </span>
                  </div>
                  {sacks.length === 0 && k !== 'FLUSH' && (
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span className="text-[13px] text-muted">
                        <BilingualLabel k="process.weighOut.none" />
                      </span>
                      <div className="w-44">
                        <YesNo
                          value={zero ? true : null}
                          onChange={(v) =>
                            update((d) => ({
                              ...d,
                              zero_streams: v
                                ? [...new Set([...d.zero_streams, k])]
                                : d.zero_streams.filter((z) => z !== k),
                            }))
                          }
                        />
                      </div>
                    </div>
                  )}
                  {sacks.length > 0 && (
                    <ul className="mt-1">
                      {sacks.map((o) => (
                        <li key={o.container_id} className="flex items-center justify-between gap-2 text-[13px]">
                          <span className="font-mono">{o.qr_payload}</span>
                          <span className="flex items-center gap-1">
                            <span className="font-mono tabular">{fmt.weight(o.net_g)}</span>
                            {o.stream === 'CONV' && <Badge tone="danger">{t('process.tie.redShort')}</Badge>}
                            <IconButton
                              size="sm"
                              label={t('process.weighOut.void')}
                              icon={<Trash />}
                              onClick={() => setVoiding(o)}
                            />
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )
            })}
          </ul>
        </CardBody>
      </Card>

      <BalancePanel state={state} run={run} />

      {gaps.length > 0 && (
        <Banner tone="warn" title={t('process.close.notYet')}>
          <ul className="list-disc pl-5">
            {gaps.map((g, i) => (
              <li key={i}>{gapText(g)}</li>
            ))}
          </ul>
        </Banner>
      )}
      <Button size="xl" fullWidth disabled={gaps.length > 0} loading={closing} onClick={() => void close()}>
        <BilingualLabel k="process.close.button" />
      </Button>

      <Sheet
        open={!!voiding}
        onClose={() => setVoiding(null)}
        title={<BilingualLabel k="process.weighOut.void" />}
        description={voiding ? `${voiding.qr_payload} · ${fmt.weight(voiding.net_g)}` : undefined}
        footer={
          <Button variant="danger" size="xl" fullWidth disabled={!voidReason} onClick={() => void voidSack()}>
            <BilingualLabel k="process.weighOut.void" />
          </Button>
        }
      >
        <ChoiceGroup
          layout="stack"
          options={VOID_REASONS.map((r) => ({ value: r, label: t(`process.void.${r}` as MessageKey) }))}
          value={voidReason}
          onChange={setVoidReason}
        />
      </Sheet>
    </div>
  )
}
